import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import type { PropertyMappingProps, PropertyMappingViewModel } from "./PropertyMapping.types";
import type { PropertyMappings } from "../../../../../shared/PropertyMappings.types";
import type {
	PropertyWithExamples,
	AggregatedAutoGuessResult,
} from "../../../components/GeoTypeSelectorGroup/GeoTypeSelectorGroup.types";
import type { GeodataGroup } from "../../WizardContainer";
import { autoGuessPropertyMappings } from "../../../components/PropertyMappingSelector/PropertyMappingSelector.helpers";

/**
 * Extracts unique properties with examples from all groups' features
 */
export function extractAllAvailableProperties(geodataGroups: GeodataGroup[]): PropertyWithExamples[] {
	const propertyMap = new Map<string, Set<string>>();

	for (const group of geodataGroups) {
		for (const feature of group.geoJSON.features) {
			if (feature.properties) {
				for (const [key, value] of Object.entries(feature.properties)) {
					if (!propertyMap.has(key)) {
						propertyMap.set(key, new Set());
					}
					if (value !== null && value !== undefined) {
						const stringValue = String(value);
						// Only add if we have fewer than 5 examples
						const examples = propertyMap.get(key)!;
						if (examples.size < 5) {
							examples.add(stringValue);
						}
					}
				}
			}
		}
	}

	return Array.from(propertyMap.entries())
		.map(([name, examples]) => ({
			name,
			examples: Array.from(examples),
		}))
		.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Aggregates auto-guessed property mappings from all groups
 */
export function aggregateAutoGuesses(
	geodataGroups: GeodataGroup[],
	allProperties: PropertyWithExamples[],
): AggregatedAutoGuessResult | null {
	if (geodataGroups.length === 0) return null;

	// Create a map for quick property lookup
	const propertyMap = new Map(allProperties.map((p) => [p.name, p]));

	// Aggregate auto-guesses from all groups
	const result: AggregatedAutoGuessResult = {
		grower: null,
		farm: null,
		field: null,
		fieldId: null,
		labName: null,
		labInstructions: null,
		submissionNotes: null,
		yieldGoal: null,
		submitterName: null,
		submitterEmail: null,
		growerPhoneNumber: null,
		eventId: null,
		labSubmissionCode: null,
		externalId: null,
		externalFarmId: null,
		externalLabId: null,
		dateFieldReadyEstimated: null,
		density: null,
		depth: null,
		frequencyOfMicros: null,
		testPackage: null,
		fieldPriority: null,
		nextCrop: null,
		lastCrop: null,
		branch: null,
		salesperson: null,
		salespersonMobilePhone: null,
	};

	// Collect unique guesses across all groups
	const guessedByField = new Map<string, Set<string>>();

	for (const group of geodataGroups) {
		// Pass the group's GeoJSON to autoGuessPropertyMappings
		const groupGuesses = autoGuessPropertyMappings([group.geoJSON]);

		for (const [field, value] of Object.entries(groupGuesses)) {
			if (value) {
				if (!guessedByField.has(field)) {
					guessedByField.set(field, new Set());
				}
				guessedByField.get(field)!.add(value);
			}
		}
	}

	// Convert to PropertyWithExamples arrays
	for (const [field, names] of guessedByField.entries()) {
		const props: PropertyWithExamples[] = [];
		for (const name of names) {
			const prop = propertyMap.get(name);
			if (prop) {
				props.push(prop);
			}
		}
		if (props.length > 0 && field in result) {
			(result as unknown as Record<string, PropertyWithExamples[] | null>)[field] = props;
		}
	}

	return result;
}

/**
 * Creates an empty property mappings object
 */
export function createEmptyPropertyMappings(): PropertyMappings {
	return {
		grower: null,
		farm: null,
		field: null,
		fieldId: null,
		labName: null,
		labInstructions: null,
		submissionNotes: null,
		yieldGoal: null,
		submitterName: null,
		submitterEmail: null,
		growerPhoneNumber: null,
		eventId: null,
		labSubmissionCode: null,
		externalId: null,
		externalFarmId: null,
		externalLabId: null,
		dateFieldReadyEstimated: null,
		density: null,
		depth: null,
		frequencyOfMicros: null,
		testPackage: null,
		fieldPriority: null,
		nextCrop: null,
		lastCrop: null,
		branch: null,
		salesperson: null,
		salespersonMobilePhone: null,
	};
}

/**
 * Hook for PropertyMapping step logic
 */
export function usePropertyMappingViewModel(props: PropertyMappingProps): PropertyMappingViewModel {
	const {
		geodataGroups,
		propertyMappings,
		onMappingChange,
		onBatchMappingChange,
		onApplyGlobalMapping,
		onPrevious,
		onNext,
	} = props;

	// Track which groups are expanded (collapsed by default)
	const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set());

	// Track if auto-guess has been applied
	const autoGuessApplied = useRef(false);

	// Track if auto-guess notice should be shown
	const [showAutoGuessNotice, setShowAutoGuessNotice] = useState(false);

	// Track show more/less for property mapping fields
	const [showMoreFields, setShowMoreFields] = useState(false);

	const handleToggleShowMore = useCallback(() => {
		setShowMoreFields((prev) => !prev);
	}, []);

	// Computed: all available properties across all groups
	const allAvailableProperties = useMemo(() => extractAllAvailableProperties(geodataGroups), [geodataGroups]);

	// Computed: aggregated auto-guesses
	const aggregatedAutoGuesses = useMemo(
		() => aggregateAutoGuesses(geodataGroups, allAvailableProperties),
		[geodataGroups, allAvailableProperties],
	);

	// Computed: total number of groups
	const totalGroups = useMemo(() => geodataGroups.length, [geodataGroups]);

	// Auto-guess on mount (only once)
	// Uses batch update to avoid N state dispatches when processing N groups
	useEffect(() => {
		if (autoGuessApplied.current || geodataGroups.length === 0) return;

		// Collect all auto-guessed mappings in one batch
		const batchMappings: Record<string, PropertyMappings> = { ...propertyMappings };
		let appliedAny = false;

		for (const group of geodataGroups) {
			const currentMappings = propertyMappings[group.id];
			// Check if this group has any mappings set
			const hasMappings =
				currentMappings &&
				Object.values(currentMappings).some((v) => v !== null && (Array.isArray(v) ? v.length > 0 : true));

			if (!hasMappings) {
				// Run auto-guess for this group using its GeoJSON data
				const guesses = autoGuessPropertyMappings([group.geoJSON]);
				const newMappings = createEmptyPropertyMappings();

				// Convert single values to arrays
				for (const [key, value] of Object.entries(guesses)) {
					if (value) {
						(newMappings as unknown as Record<string, string[] | null>)[key] = [value];
					}
				}

				// Check if any guesses were made
				const hasGuesses = Object.values(newMappings).some(
					(v) => v !== null && (Array.isArray(v) ? v.length > 0 : true),
				);

				if (hasGuesses) {
					batchMappings[group.id] = newMappings;
					appliedAny = true;
				}
			}
		}

		// Apply all mappings in a single state update
		if (appliedAny && onBatchMappingChange) {
			onBatchMappingChange(batchMappings);
			setShowAutoGuessNotice(true);
		}
		autoGuessApplied.current = true;
	}, [geodataGroups, propertyMappings, onBatchMappingChange]);

	// Handler: Toggle group expansion
	const handleToggleGroup = useCallback((groupId: string) => {
		setExpandedGroups((prev) => {
			const next = new Set(prev);
			if (next.has(groupId)) {
				next.delete(groupId);
			} else {
				next.add(groupId);
			}
			return next;
		});
	}, []);

	// Handler: Expand all groups
	const handleExpandAll = useCallback(() => {
		setExpandedGroups(new Set(geodataGroups.map((g) => g.id)));
	}, [geodataGroups]);

	// Handler: Collapse all groups
	const handleCollapseAll = useCallback(() => {
		setExpandedGroups(new Set());
	}, []);

	// Handler: Per-group mapping change
	const handleGroupMappingChange = useCallback(
		(groupId: string, mappings: PropertyMappings) => {
			setShowAutoGuessNotice(false);
			onMappingChange(groupId, mappings);
		},
		[onMappingChange],
	);

	// Handler: Global mapping applied
	const handleGlobalMappingApply = useCallback(
		(field: keyof PropertyMappings, value: string[] | null) => {
			setShowAutoGuessNotice(false);
			onApplyGlobalMapping(field, value);
		},
		[onApplyGlobalMapping],
	);

	// Handler: Previous button
	const handlePrevious = useCallback(() => {
		onPrevious();
	}, [onPrevious]);

	// Handler: Next button
	const handleNext = useCallback(() => {
		onNext();
	}, [onNext]);

	return {
		expandedGroups,
		allAvailableProperties,
		aggregatedAutoGuesses,
		totalGroups,
		showAutoGuessNotice,
		showMoreFields,
		handleToggleShowMore,
		handleToggleGroup,
		handleExpandAll,
		handleCollapseAll,
		handleGroupMappingChange,
		handleGlobalMappingApply,
		handlePrevious,
		handleNext,
	};
}
